<template lang="pug">
.pdf-page(
  :class='{ loaded: loaded }'
  :id='`page-${proxyPage.pageNumber}`'
  :style='{ height: `${height}px`, width: `${width}px` }'
)
  // @slot Slot padrão do componente
  slot
</template>
<script>
import PDFPageProxy from 'pdfjs-dist';

/**
 * @displayName Page
 **/
export default {
  name: 'PdfPage',
  props: {
    /** Instância PDFPageProxy */
    proxyPage: {
      type: PDFPageProxy,
      required: true,
    },
  },
  data() {
    return {
      loaded: false,
      height: 0,
      width: 0,
      scale: 1,
      viewport: null,
      context: null,
      rotation: 0,
    };
  },
  created() {
    this.width = this.proxyPage.view[2];
    this.height = this.proxyPage.view[3];
  },
  mounted() {
    /** width and height are used for scale calculate, consider rotation page */
    this.rotation = this.proxyPage.rotate;
    this.scale = this.calculateScale();
    this.viewport = this.proxyPage.getViewport({
      scale: this.scale,
      rotation: this.rotation,
    });
    this.height = this.viewport.height;
    this.width = this.viewport.width;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting === true) {
          if (!this.loaded) {
            this.render();
            return;
          }

          this.verifyRender();
        }
      },
      { threshold: [0] }
    );
    observer.observe(this.$el);
  },
  methods: {
    render() {
      const canvas = document.createElement('canvas');
      this.$el.appendChild(canvas);
      canvas.height = this.viewport.height;
      canvas.width = this.viewport.width;

      this.height = this.viewport.height;
      this.width = this.viewport.width;

      this.context = canvas.getContext('2d');

      this.proxyPage.render({
        canvasContext: this.context,
        viewport: this.viewport,
      });

      this.loaded = true;

      /**
       * Escala relativa ao tamanho da página no arquivo PDF, utilizada na renderização
       * @event scale
       * @property {number} scale - Escala
       */
      this.$emit('scale', this.scale);

      /**
       * Tamanho do elemento que a página foi renderizada
       * @event element-size
       * @property {number} width - Largura do elemento
       * @property {number} height - Altura do elemento
       */
      this.$emit('element-size', {
        width: this.width,
        height: this.height,
      });

      /**
       * Tamanho da página no arquivo PDF
       * @event page-size
       * @property {number} width - Largura da página
       * @property {number} height - Altura da página
       */
      this.$emit('page-size', {
        width: this.proxyPage.view[2],
        height: this.proxyPage.view[3],
      });

      /**
       * Quanda a página é renderizada
       * @event loaded
       * @property {number} pageNumber - Numero da página
       */
      this.$emit('loaded', this.proxyPage.pageNumber);
    },
    verifyRender() {
      if (!this.context._transformMatrix) {
        this.removeCanvas();
        this.render();
      }
    },
    removeCanvas() {
      this.$el.removeChild(this.$el.lastChild);
    },
    getElementContentWidth(el) {
      const styles = window.getComputedStyle(el);
      const padding =
        parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      return el.clientWidth - padding;
    },
    getElementContentHeight(el) {
      const styles = window.getComputedStyle(el);
      const padding =
        parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      return el.clientHeight - padding;
    },
    calculateScale() {
      if (this.rotation === 0 || this.rotation === 180) {
        return (
          this.getElementContentWidth(this.$el.parentElement) /
          this.proxyPage.view[2]
        );
      }
      return (
        this.getElementContentWidth(this.$el.parentElement) /
        this.proxyPage.view[3]
      );
    },
  },
};
</script>
<docs lang="md">
#### Exemplo de uso

Renderizando uma página

```js
new Vue({
  data: () => ({
    proxyPages: [],
  }),
  template: `
    <div style='max-width: 600px; margin: auto;'>
      <document src="/assets/example.pdf" @proxy-pages="proxyPages = $event">
        <page class='item' v-if='proxyPages.length > 0' :proxy-page="proxyPages[0]"></page>
      </document>
    </div>
  `,
});
```
</docs>
